/**
 * HistoryTimelineErrorBoundary — scoped error boundary wrapping ChangelogTimeline.
 *
 * A single malformed ChangeLog row must never blank the page. This boundary
 * catches render errors inside the timeline and shows a scoped fallback card,
 * leaving the surrounding page (filters, header, pagination) functional.
 *
 * i18n: the fallback reads keys audit:error_some_entries and audit:error_reload.
 * Because class components cannot use hooks we pass translated strings as props
 * from the functional parent, with safe defaults for the (rare) case where i18n
 * hasn't loaded yet.
 */

import { Component, type ReactNode } from "react";
import i18n from "../i18n";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

export class HistoryTimelineErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: { componentStack?: string }): void {
    // eslint-disable-next-line no-console
    console.error("[HistoryTimeline]", error, info.componentStack);
  }

  render(): ReactNode {
    if (this.state.hasError) {
      const errorMsg = i18n.t("audit:error_some_entries", {
        defaultValue: "Some history entries could not be displayed.",
      });
      const reloadLabel = i18n.t("audit:error_reload", {
        defaultValue: "Reload",
      });

      return (
        <div className="px-4 py-6 text-center">
          <p className="text-[13px] text-muted">{errorMsg}</p>
          <button
            type="button"
            onClick={() => this.setState({ hasError: false })}
            className="mt-2 text-[12px] text-accent hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/30 rounded"
          >
            {reloadLabel}
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
